import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadAttachments } from '@api/attachments';
import type { AppManifest, AppRunDetail, ViewSnapshot } from '@api/apps';
import useConversations from '@contexts/useConversations';
import { fetchArtifactBlob } from '@hooks/useModelBlob';
import { isOwnApiUrl } from '@utils/apiOrigin';
import { useAppRun, useAppRunActions } from '../useApps';
import { StreamRequestError, useAppRunStream } from '../useAppRunStream';
import { useAppRunSetup } from '../runSetup/useAppRunSetup';
import type { AutostartRequest, RunHost } from './runHost';

export interface StartRequest {
  readonly prompt?: string;
  readonly options: Record<string, unknown>;
  readonly files: readonly File[];
  /** Which inputs to keep: on a draft, the ones it starts on; on a run that ran, the ones the new run takes. Omitted: as many as fit beside `files`. */
  readonly keepInputIds?: readonly string[];
  readonly example?: boolean;
}

export interface RefineRequest {
  readonly text?: string;
  readonly files: readonly File[];
  readonly marks?: readonly { kind: 'box' | 'freehand'; points: readonly (readonly [number, number])[] }[];
  readonly viewSnapshot?: ViewSnapshot;
}

export interface AnswerRequest {
  readonly answers: readonly Record<string, unknown>[];
  readonly files: readonly File[];
  readonly viewSnapshot?: ViewSnapshot;
}

/** The example's files, fetched so they are uploaded exactly like files the user picked. */
const exampleFiles = async (manifest: AppManifest): Promise<File[]> => {
  const inputs = manifest.presentation?.example?.inputs ?? [];
  return Promise.all(
    inputs.map(async (input) => {
      const blob = await fetchArtifactBlob(input.url, isOwnApiUrl(input.url));
      return new File([blob], input.filename, { type: input.contentType, lastModified: 0 });
    }),
  );
};

/** The example's settings win over the page's; its first instruction is used unless the page sent one of its own. */
const withExample = (manifest: AppManifest, request: StartRequest): Pick<StartRequest, 'options' | 'prompt'> => {
  if (!request.example) return { options: request.options, prompt: request.prompt };
  const example = manifest.presentation?.example;
  return {
    options: { ...request.options, ...example?.options },
    prompt: request.prompt ?? example?.prompt ?? undefined,
  };
};

interface StagedFile {
  readonly identity: string;
  readonly attachmentId: string;
}

// Metadata rather than bytes: an edit on disk moves lastModified, and a retry must not re-read a large model.
const identityOf = (file: File): string => [file.name, file.size, file.lastModified, file.type].join('|');

// Each staged upload stands in for one file of a request, so two identical picks stay two inputs.
const claimStaged = (unclaimed: StagedFile[], file: File): string | undefined => {
  const index = unclaimed.findIndex((entry) => entry.identity === identityOf(file));
  return index < 0 ? undefined : unclaimed.splice(index, 1)[0].attachmentId;
};

const reusedIds = (files: readonly File[], staged: readonly StagedFile[]): (string | undefined)[] => {
  const unclaimed = [...staged];
  return files.map((file) => claimStaged(unclaimed, file));
};

const stageFiles = async (
  conversationId: string,
  files: readonly File[],
  staged: StagedFile[],
  reused = reusedIds(files, staged),
): Promise<string[]> => {
  const fresh = files.filter((_, index) => reused[index] === undefined);
  const uploaded = fresh.length > 0 ? await uploadAttachments(conversationId, fresh) : [];
  uploaded.forEach((attachment, index) =>
    staged.push({ identity: identityOf(fresh[index]), attachmentId: attachment.attachmentId }),
  );
  let next = 0;
  return reused.map((id) => id ?? uploaded[next++].attachmentId);
};

// A file named like an input replaces it, as the studio's own picker treats a second file of that name.
const keptInputIds = (
  inputs: AppRunDetail['inputs'],
  request: StartRequest,
  files: readonly File[],
  maxFiles: number,
): string[] => {
  if (request.example) return [];
  const named = new Set(files.map((file) => file.name.toLowerCase()));
  const listed = request.keepInputIds ? new Set(request.keepInputIds) : null;
  const kept = inputs
    .filter((input) => !listed || listed.has(input.attachmentId))
    .filter((input) => !named.has(input.filename.toLowerCase()))
    .map((input) => input.attachmentId);
  return listed ? kept : kept.slice(0, Math.max(0, maxFiles - files.length));
};

const requireRoom = (maxFiles: number, count: number) => {
  if (count > maxFiles) throw new Error(`This app takes at most ${maxFiles} file(s); you added ${count}.`);
};

/**
 * One app run's life on a screen: opening it, starting, refining, answering
 * and stopping its turns, and attaching to a turn already running. Where the
 * run comes from and where it is shown is the host's business.
 *
 * <p>Without a run id this is a composer: nothing exists server-side, so opening
 * an app and walking away leaves no conversation behind. With one, the run row
 * is the source of truth and the stream only decorates it while a turn is in
 * flight, which is why a reload or a second tab still lands on the truth.
 */
export const useAppRunController = (host: RunHost) => {
  const { appKey, runId } = host;
  // Cancel only asks the orchestrator to stop; the row stays RUNNING until the
  // turn is finalised, so the screen holds the stop itself until then.
  const [stopRequestedFor, setStopRequestedFor] = useState<string | null>(null);
  // The stream ends a turn before the row is finalised; until the row catches up it is polled quickly.
  const [settlingFor, setSettlingFor] = useState<string | null>(null);
  const { data: run, isLoading, isError, refetch } = useAppRun(
    runId,
    runId !== undefined && (stopRequestedFor === runId || settlingFor === runId),
  );
  const { rerun, cancel, remove } = useAppRunActions();
  const { updateConversation } = useConversations();
  const [isLaunching, setIsLaunching] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [liveTitle, setLiveTitle] = useState<{ conversationId: string; title: string } | null>(null);
  // A turn is claimed the instant it is asked for: nothing else reads as busy
  // until its POST lands, so a second request would race the first and be
  // refused as a concurrent turn. The id tells a superseded turn it no longer owns it.
  const [submitting, setSubmitting] = useState(false);
  const turnOwnerRef = useRef<number | null>(null);
  const turnSeqRef = useRef(0);
  // A start refused after its upload is retried with copies of the same files,
  // which must not be staged on the draft a second time. Keyed by run, so a
  // hand-over's upload is reused by the run it opened and by no other.
  const stagedRef = useRef<{ runId: string | null; files: StagedFile[] }>({ runId: null, files: [] });

  const refetchRun = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  const { live, start, refine, attach, stop, clearStreamError, settleApproval } = useAppRunStream({
    run: run ?? null,
    onTurnStart: refetchRun,
    onTurnEnd: () => {
      setSettlingFor(runId ?? null);
      refetchRun();
    },
    onInputPreview: refetchRun,
    onTitle: (conversationId, title) => {
      setLiveTitle({ conversationId, title });
      updateConversation(conversationId, { title });
    },
  });

  // A run in flight is never reinterpreted by a newer version of the app.
  const manifest: AppManifest | null = run?.manifest ?? host.composeManifest;
  const runSetup = useAppRunSetup(appKey, (host.composeManifest ?? manifest)?.runsOn, run ?? null, host.setupTarget);

  // Moving to another run reuses this screen, so nothing held for the old one
  // may be read as the new one's.
  useEffect(() => {
    turnOwnerRef.current = null;
    setSubmitting(false);
    setStopRequestedFor(null);
    setActionError(null);
  }, [runId]);

  const hasStarted = Boolean(run) && run?.status !== 'DRAFT';
  const stopRequested = stopRequestedFor !== null && stopRequestedFor === run?.runId;
  const stopping = run?.status === 'RUNNING' && stopRequested;

  // A stream this screen only attached to can miss its end (the server restarted
  // mid-turn); once the row says the turn is over, the row wins.
  const followingRef = useRef<string | null>(null);

  // A turn may already be in flight when this screen mounts — a reload, a second
  // tab, or a run started elsewhere. Attaching shows it live.
  const attachedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run || stopping || attachedRef.current === run.runId) return;
    if (run.status !== 'RUNNING' || live.isStreaming) return;
    // A row still RUNNING on the turn this screen's own stream saw end is stale.
    if (live.endedTurn !== null && run.turnStartedAt === live.endedTurn) return;
    attachedRef.current = run.runId;
    followingRef.current = run.runId;
    attach(run.conversationId).catch(() => undefined);
  }, [attach, live.endedTurn, live.isStreaming, run, stopping]);

  useEffect(() => {
    if (!run || !live.isStreaming || followingRef.current !== run.runId) return;
    if (run.status === 'RUNNING' || run.status === 'DRAFT') return;
    followingRef.current = null;
    stop();
  }, [live.isStreaming, run, stop]);

  // The stream's own end confirms a stop promptly (the row is polled far less
  // often), so it stays open until the row reads CANCELLED.
  useEffect(() => {
    if (run?.status !== 'CANCELLED' || stopRequestedFor !== run.runId) return;
    stop();
    setStopRequestedFor(null);
  }, [run, stop, stopRequestedFor]);

  if (settlingFor !== null && run?.runId === settlingFor && run.status !== 'RUNNING') setSettlingFor(null);

  // A row still RUNNING on the turn this screen's own stream saw end is only waiting to be finalised.
  const turnEnded =
    run?.status === 'RUNNING' && !live.isStreaming && live.endedTurn !== null && run.turnStartedAt === live.endedTurn;
  const isBusy = !stopRequested && (live.isStreaming || (run?.status === 'RUNNING' && !turnEnded) || isLaunching);

  const runTurn = useCallback(
    async (turn: () => Promise<void>) => {
      if (turnOwnerRef.current !== null) throw new TurnInFlight();
      const owned = ++turnSeqRef.current;
      turnOwnerRef.current = owned;
      followingRef.current = null;
      setSubmitting(true);
      setActionError(null);
      clearStreamError();
      try {
        await turn();
      } catch (error) {
        if (turnOwnerRef.current === owned) {
          setActionError(error instanceof Error ? error.message : 'That did not work. Try again.');
        }
        throw error;
      } finally {
        if (turnOwnerRef.current === owned) {
          turnOwnerRef.current = null;
          setSubmitting(false);
        }
      }
    },
    [clearStreamError],
  );

  // A run opened from the composer is a draft for the moment in between, so
  // it starts itself once its row is here.
  const { autostartFor } = host;
  const autostartedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run || autostartedRef.current === run.runId || run.status !== 'DRAFT') return;
    const requested = autostartFor(run.runId);
    if (!requested) return;
    autostartedRef.current = run.runId;
    runTurn(() => start({ options: requested.options, prompt: requested.prompt, ...requested.execution })).catch(
      () => undefined,
    );
  }, [autostartFor, run, runTurn, start]);

  // The hand-over ends when the row it opened is here, not when the host shows it.
  const launchingForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run || run.runId !== launchingForRef.current) return;
    launchingForRef.current = null;
    setIsLaunching(false);
  }, [run]);

  // Serves both the first run and a re-run, which differ only in where the new
  // run comes from. A failure part-way deletes it again rather than stranding
  // an empty conversation in the task list.
  const { showRun } = host;
  const handOver = useCallback(
    async (open: () => Promise<AppRunDetail>, files: readonly File[], request: AutostartRequest) => {
      setIsLaunching(true);
      let opened: AppRunDetail | null = null;
      try {
        opened = await open();
        launchingForRef.current = opened.runId;
        const staged: StagedFile[] = [];
        if (files.length > 0) await stageFiles(opened.conversationId, files, staged);
        stagedRef.current = { runId: opened.runId, files: staged };
        showRun(opened.runId, request);
      } catch (error) {
        if (opened) await remove(opened.runId).catch(() => undefined);
        launchingForRef.current = null;
        setIsLaunching(false);
        throw error;
      }
    },
    [remove, showRun],
  );

  // A refusal keeps what was staged for the retry, unless the server no longer has it.
  const startDraft = useCallback(
    async (draft: AppRunDetail, request: StartRequest, files: readonly File[], kept: readonly string[]) => {
      if (stagedRef.current.runId !== draft.runId) stagedRef.current = { runId: draft.runId, files: [] };
      const staged = stagedRef.current.files;
      const reused = reusedIds(files, staged);
      const known = new Set([...kept, ...reused.filter((id) => id !== undefined)]);
      requireRoom(draft.manifest.inputs.maxFiles, known.size + reused.filter((id) => id === undefined).length);
      const uploaded = await stageFiles(draft.conversationId, files, staged, reused);
      const attachmentIds = [...new Set([...kept, ...uploaded])];
      try {
        await start({
          options: request.options,
          prompt: request.prompt,
          attachmentIds,
          ...runSetup.selection,
        });
      } catch (error) {
        if (error instanceof StreamRequestError && error.status === 404) staged.splice(0);
        throw error;
      }
      staged.splice(0);
    },
    [runSetup.selection, start],
  );

  const { openRun } = host;
  const startRun = useCallback(
    async (request: StartRequest) => {
      if (!appKey || !manifest) return;
      const files = request.example ? await exampleFiles(manifest) : request.files;
      const { options, prompt } = withExample(manifest, request);
      const launch: AutostartRequest = { options, prompt, execution: runSetup.selection };
      await runTurn(async () => {
        // Refused here too, before a run is created that the server would only refuse to start.
        if (runSetup.blockedReason) throw new Error(runSetup.blockedReason);
        if (!run) {
          requireRoom(manifest.inputs.maxFiles, files.length);
          await handOver(openRun, files, launch);
          return;
        }
        const kept = keptInputIds(run.inputs, request, files, manifest.inputs.maxFiles);
        // A run that has already been somewhere is never re-entered: changed
        // inputs are a new attempt, and the old one keeps what it ran on.
        if (hasStarted) {
          requireRoom(manifest.inputs.maxFiles, kept.length + files.length);
          await handOver(() => rerun({ runId: run.runId, keepAttachmentIds: kept }), files, launch);
          return;
        }
        await startDraft(run, { ...request, options, prompt }, files, kept);
      });
    },
    [appKey, handOver, hasStarted, manifest, openRun, rerun, run, runSetup.blockedReason, runSetup.selection, runTurn, startDraft],
  );

  const refineRun = useCallback(
    (request: RefineRequest) =>
      runTurn(async () => {
        if (!run) throw new NoRun();
        const [annotated] = request.files.length > 0 ? await uploadAttachments(run.conversationId, [...request.files]) : [];
        await refine({
          text: request.text,
          marks: request.marks?.map((mark) => ({ kind: mark.kind, points: mark.points.map(([x, y]) => [x, y] as const) })),
          annotatedAttachmentId: annotated?.attachmentId,
          viewSnapshot: request.viewSnapshot,
        });
      }),
    [refine, run, runTurn],
  );

  // A crop the user drew as an answer is uploaded first, so the folded legend can name it.
  const answerRun = useCallback(
    (request: AnswerRequest) =>
      runTurn(async () => {
        if (!run) throw new NoRun();
        const uploaded = request.files.length > 0 ? await uploadAttachments(run.conversationId, [...request.files]) : [];
        await refine({
          questionAnswers: [...request.answers],
          answerAttachmentIds: uploaded.length > 0 ? uploaded.map((file) => file.attachmentId) : undefined,
          viewSnapshot: request.viewSnapshot,
        });
      }),
    [refine, run, runTurn],
  );

  const cancelRun = useCallback(async () => {
    if (!run) throw new NoRun();
    await cancel(run.runId);
    setStopRequestedFor(run.runId);
  }, [cancel, run]);

  const { openTranscript: showTranscript, startOver } = host;
  const openTranscript = useCallback(() => {
    if (!run) throw new NoRun();
    showTranscript(run);
  }, [run, showTranscript]);

  return {
    appKey,
    runId,
    run: run ?? null,
    manifest,
    live,
    isBusy,
    stopping,
    submitting,
    isLaunching,
    isLoading: (Boolean(runId) && isLoading) || (!runId && host.composeLoading),
    notFound: Boolean(runId) && !isLoading && (isError || !run),
    readOnly: run?.readOnly ?? false,
    testRuns: host.testRuns,
    crossOrg: run ? Boolean(run.crossOrg) : host.composeCrossOrg,
    error: actionError ?? live.streamError,
    title: run && liveTitle?.conversationId === run.conversationId ? liveTitle.title : (run?.title ?? null),
    startRun,
    refineRun,
    answerRun,
    cancelRun,
    openTranscript,
    newRun: startOver,
    runSetup,
    settleApproval,
  };
};

export type AppRunController = ReturnType<typeof useAppRunController>;

/**
 * What the frame and its bridge read of a controller. A capability the page
 * gains goes here first, so every controller (the signed-out visitor's too)
 * has to decide what it does with it.
 */
export type AppFrameController = Pick<
  AppRunController,
  | 'appKey'
  | 'manifest'
  | 'run'
  | 'readOnly'
  | 'live'
  | 'isBusy'
  | 'submitting'
  | 'stopping'
  | 'error'
  | 'startRun'
  | 'refineRun'
  | 'answerRun'
  | 'cancelRun'
  | 'openTranscript'
  | 'newRun'
  | 'testRuns'
> & {
  /** Present for a visitor without an account: opens the sign-up gate instead of spending. */
  requireAccount?: () => void;
};

/** A second turn asked for while one is still being sent. */
export class TurnInFlight extends Error {
  constructor() {
    super('The app is already working on a request.');
    this.name = 'TurnInFlight';
  }
}

export class NoRun extends Error {
  constructor() {
    super('This app has not run yet.');
    this.name = 'NoRun';
  }
}
